import LinkBehavior from '@/components/LinkBehavior';
import { Theme } from '@mui/material/styles';

declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    auto: true;
    xLarge: true;
  }
  interface ButtonVariantsOverrides {
    free: true;
  }
}

export const BUTTON_SIZES = {
  // 고정 사이즈 (높이 강제)
  small: { height: 36, px: '8px', fontSize: '12px' },
  medium: { height: 40, px: '14px', fontSize: '14px' },
  large: { height: 44, px: '16px', fontSize: '16px' },
  xLarge: { height: 56, px: '16px', fontSize: '16px' },
  // 오토 사이즈 (높이 유동적, 패딩으로 조절)
  auto: { py: 1, px: 1.5, fontSize: '14px' },
};

export default function Button(theme: Theme) {
  return {
    MuiButton: {
      defaultProps: {
        disableElevation: true, // 그림자 제거
        variant: 'free', // 기본은 박스 형태
        size: 'auto', // 기본 사이즈
        // Button에 href가 들어오면 자동으로 LinkBehavior 사용하도록 추가
        LinkComponent: LinkBehavior,
      },

      styleOverrides: {
        root: {
          minWidth: 'auto',
          borderRadius: '6px', // 둥글기 통일
          lineHeight: 'normal',
          textTransform: 'none', // 대문자 강제 변환 해제
          '&:hover': {
            backgroundColor: 'var(--dk-bg-muted, #F3F5F6)',
          },
          // 아이콘과 텍스트 간격 미세 조정
          '& .MuiButton-startIcon': { marginLeft: '0', marginRight: '4px' },
          '& .MuiButton-endIcon': { marginLeft: '4px', marginRight: '0' },
          '& .MuiButton-loadingIndicator': {
            color: 'inherit', // 버튼 텍스트 색상을 따라가도록 설정
          },
        },

        // 3. 사이즈별 스타일 재정의 (Size Overrides)
        sizeSmall: {
          height: BUTTON_SIZES.small.height,
          paddingLeft: theme.spacing(BUTTON_SIZES.small.px),
          paddingRight: theme.spacing(BUTTON_SIZES.small.px),
          fontSize: BUTTON_SIZES.small.fontSize,
        },
        sizeMedium: {
          height: BUTTON_SIZES.medium.height,
          paddingLeft: theme.spacing(BUTTON_SIZES.medium.px),
          paddingRight: theme.spacing(BUTTON_SIZES.medium.px),
          fontSize: BUTTON_SIZES.medium.fontSize,
        },
        sizeLarge: {
          height: BUTTON_SIZES.large.height,
          paddingLeft: theme.spacing(BUTTON_SIZES.large.px),
          paddingRight: theme.spacing(BUTTON_SIZES.large.px),
          fontSize: BUTTON_SIZES.large.fontSize,
        },
        sizeXLarge: {
          height: BUTTON_SIZES.xLarge.height,
          paddingLeft: theme.spacing(BUTTON_SIZES.xLarge.px),
          paddingRight: theme.spacing(BUTTON_SIZES.xLarge.px),
          fontSize: BUTTON_SIZES.xLarge.fontSize,
        },
        sizeAuto: {
          height: 'auto',
          // 내용물에 따라 늘어나도록 패딩으로 공간 확보
          paddingTop: theme.spacing(BUTTON_SIZES.auto.py),
          paddingBottom: theme.spacing(BUTTON_SIZES.auto.py),
          paddingLeft: theme.spacing(BUTTON_SIZES.auto.px),
          paddingRight: theme.spacing(BUTTON_SIZES.auto.px),
          fontWeight: '400',
          fontSize: '0.875rem',
        },

        // 4. 형태별 스타일 재정의 (Variant Overrides)
        // contained
        // 검정 버튼 — 앱 웹뷰 다크 모드에서는 밝은 버튼으로 뒤집는다 (styles/appDarkMode.ts inverse)
        contained: {
          backgroundColor: 'var(--dk-inverse-bg, black)',
          color: 'var(--dk-inverse-text, white)',
          '&:hover': {
            backgroundColor: 'var(--dk-inverse-bg-hover, #282828)',
            color: 'var(--dk-inverse-text, white)',
          },
          '&.Mui-disabled': {
            backgroundColor: 'var(--dk-bg-strong, #D1D8E2)',
            color: 'var(--dk-text-dim, white)',
          },
        },
        // text
        text: {
          backgroundColor: 'transparent',
          color: 'var(--dk-text-secondary, #888D96)',
          '&:hover': {
            textDecoration: 'underline',
            color: 'var(--dk-text, black)',
          },
          '&.Mui-disabled': {
            color: 'var(--dk-text-disabled, #D1D8E2)',
          },
        },
        // outlined
        outlined: {
          border: '1px solid var(--dk-line, #E7E9EC)',
          color: 'var(--dk-text, black)',
          backgroundColor: 'var(--dk-bg, white)',
          '&:hover': {
            backgroundColor: 'var(--dk-bg, white)',
            borderColor: 'var(--dk-text, black)',
          },
          '&.Mui-disabled': {
            borderColor: 'var(--dk-line-strong, #D1D8E2)',
            color: 'var(--dk-text-disabled, #D1D8E2)',
          },
        },

        free: {
          color: 'var(--variant-textColor)',
        },
      },
    },
  };
}
